import React from "react";
import { MasterComplianceCalendarEntity } from "../../domain/entities/MasterComplianceCalendarEntity";
import { Table } from "../../../../ui/components/Table";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import {
  FeatherEdit,
  FeatherTrash2,
} from "@subframe/core";

interface CalendarEntriesTableProps {
  entries: MasterComplianceCalendarEntity[];
  onEdit: (entry: MasterComplianceCalendarEntity) => void;
  onDelete: (entryId: number) => void;
  deletingEntryId?: number | null;
}

export const CalendarEntriesTable: React.FC<CalendarEntriesTableProps> = ({
  entries,
  onEdit,
  onDelete,
  deletingEntryId,
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (entries.length === 0) {
    return (
      <div className="flex w-full items-center justify-center p-8 text-body font-body text-subtext-color">
        No calendar entries found
      </div>
    );
  }

  return (
    <Table
      header={
        <Table.HeaderRow>
          <Table.HeaderCell>Form Name</Table.HeaderCell>
          <Table.HeaderCell>Period</Table.HeaderCell>
          <Table.HeaderCell>Period Start</Table.HeaderCell>
          <Table.HeaderCell>Period End</Table.HeaderCell>
          <Table.HeaderCell>Due Date</Table.HeaderCell>
          <Table.HeaderCell>Authority</Table.HeaderCell>
          <Table.HeaderCell>Status</Table.HeaderCell>
          <Table.HeaderCell>Actions</Table.HeaderCell>
        </Table.HeaderRow>
      }
    >
      {entries.map((entry) => (
        <Table.Row key={entry.id}>
          <Table.Cell>
            <span className="text-body-bold font-body-bold text-default-font">
              {entry.compliance_form_name}
            </span>
          </Table.Cell>
          <Table.Cell>
            <span className="text-body font-body text-neutral-500">
              {entry.period}
            </span>
          </Table.Cell>
          <Table.Cell>
            <span className="text-body font-body text-neutral-500">
              {formatDate(entry.period_start_date)}
            </span>
          </Table.Cell>
          <Table.Cell>
            <span className="text-body font-body text-neutral-500">
              {formatDate(entry.period_end_date)}
            </span>
          </Table.Cell>
          <Table.Cell>
            <span className="text-body font-body text-neutral-500">
              {formatDate(entry.due_date)}
            </span>
          </Table.Cell>
          <Table.Cell>
            <span className="text-body font-body text-neutral-500">
              {entry.authority}
            </span>
          </Table.Cell>
          <Table.Cell>
            <Badge variant={entry.is_active ? "success" : "error"}>
              {entry.is_active ? "Active" : "Inactive"}
            </Badge>
          </Table.Cell>
          <Table.Cell>
            <div className="flex items-center gap-2">
              <Button
                variant="neutral-tertiary"
                size="small"
                icon={<FeatherEdit />}
                onClick={() => onEdit(entry)}
              >
                Edit
              </Button>
              <Button
                variant="destructive-tertiary"
                size="small"
                icon={<FeatherTrash2 />}
                onClick={() => {
                  if (window.confirm("Are you sure you want to delete this entry?")) {
                    onDelete(entry.id);
                  }
                }}
                loading={deletingEntryId === entry.id}
                disabled={deletingEntryId === entry.id}
              >
                Delete
              </Button>
            </div>
          </Table.Cell>
        </Table.Row>
      ))}
    </Table>
  );
};

